<template>
  <div class="dashboard">
    <!-- 时间选择 -->
    <div class="time-range">
      <button
        v-for="range in timeRanges"
        :key="range.value"
        :class="['time-range-button', { active: selectedTimeRange === range.value }]"
        @click="selectTimeRange(range.value)"
      >
        {{ range.label }}
      </button>
    </div>

    <!-- 数据指标卡片 -->
    <div class="data-metrics">
      <div class="metric-item">
        <div class="metric-label">浏览量(PV)</div>
        <div class="metric-value">{{ (overviewData.totalPageVisit) }}</div>
      </div>
      <div class="metric-item">
        <div class="metric-label">IP数</div>
        <div class="metric-value">{{ formatNumber(overviewData.totalIpVisit) }}</div>
      </div>
      <div class="metric-item">
        <div class="metric-label">平均浏览时长</div>
        <div class="metric-value">{{ formatSeconds(overviewData.totalAvgDuration) }}</div>
      </div>
    </div>

    <!-- 指标切换 -->
    <div class="indicator-switcher">
      <label>指标：</label>
      <select v-model="selectedIndicator" @change="handleIndicatorChange">
        <option value="pv">浏览量(PV)</option>
        <option value="ip">IP数</option>
        <option value="duration">平均浏览时长</option>
      </select>
    </div>

    <!-- 数据展示 -->
    <div class="data-display">
      <!-- 数据表格 -->
      <div class="data-table">
        <div v-if="loading" class="loading">数据加载中...</div>
        <table v-else>
          <thead>
            <tr>
              <th>省份</th>
              <th>{{ currentIndicator.label }}</th>
              <th>占比</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in sortedProvinceData" :key="index">
              <td>{{ item.province }}</td>
              <td>{{ formatValue(item) }}</td>
              <td>{{ formatPercentage(item) }}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted } from 'vue';
import axios from 'axios';
import { useRoute } from 'vue-router';  
import { shallowRef, onUnmounted, reactive,onBeforeUnmount } from 'vue';
import http from '@/shared/http.service';
import { Uris, } from '@/shared/const';
import { Subject, } from 'rxjs';
import { catchError, switchMap, } from 'rxjs/operators';
const route = useRoute();
const query = new URLSearchParams(window.location.search);
const articleId = query.get('id');

// 时间范围配置
const timeRanges = ref([
  { label: '今天', value: 'today' },
  { label: '最近7天', value: 'last7Days' },
  { label: '最近30天', value: 'last30Days' },
]);

// 响应式数据
const selectedTimeRange = ref('today');
const selectedIndicator = ref('pv');
const overviewData = ref({ 
  totalPageVisit: 0,
  totalIpVisit: 0,
  totalAvgDuration: 0 
});
const provinceData = ref([]);
const loading = ref(false);

// 指标配置
const indicators = {
  pv: {
    label: '浏览量(PV)',
    key: 'pageVisit',
    percentage: 'pvPercentage',
    formatter: v => formatNumber(v)
  },
  ip: {
    label: 'IP数',
    key: 'ipVisit',
    percentage: 'ipPercentage',
    formatter: v => formatNumber(v)
  },
  duration: {
    label: '平均时长',
    key: 'avgDuration',
    percentage: 'durationPercentage',
    formatter: v => formatSeconds(v)
  }
};

// 计算属性
const currentIndicator = computed(() => indicators[selectedIndicator.value]);
const sortedProvinceData = computed(() => {
  return [...provinceData.value].sort((a, b) => 
    b[currentIndicator.value.key] - a[currentIndicator.value.key]
  );
});

// 获取日期范围
const getDateRange = () => {
  const now = new Date();
  const start = new Date();
  const end = new Date();
  
  switch(selectedTimeRange.value) {
    case 'today':
      start.setHours(0, 0, 0, 0); 
      end.setHours(23, 59, 59, 999);
      break;
    case 'last7Days':
      start.setDate(start.getDate() - 6);
      start.setHours(0, 0, 0, 0);
      break;
    case 'last30Days':
      start.setDate(start.getDate() - 29);
      start.setHours(0, 0, 0, 0);
      break;
  }
  return { start, end };
};

// 数据获取
const fetchData = async () => {
  try {
    loading.value = true;
    const { start, end } = getDateRange();
    
    // 获取总览数据
    //const overviewRes = await http.get(`${Uris.GetArticleVisitRecord}?articleId=${articleId}&startDate=${start.toISOString()}&endDate=${end.toISOString()}`,{});
    http.get(`${Uris.GetArticleVisitRecord}?articleId=${articleId}&startDate=${start.toLocaleString()}&endDate=${end.toLocaleString()}`).subscribe(t=>{
      overviewData.value = t; 
    });
    
    var targetsType=selectedIndicator.value === 'duration' ? 3 : selectedIndicator.value === 'ip' ? 2 : 1;
    // 获取省份数据
    http.get(`${Uris.GetArticleVisitRecordByProvince}?articleId=${articleId}&startDate=${start.toLocaleString()}&endDate=${end.toLocaleString()}&targetsType=${targetsType}`).subscribe(t=>{
      provinceData.value = t;
    });
    
  } catch (error) {
    console.error('数据获取失败:', error);
  } finally {
    loading.value = false;
  }
};

// 格式工具
const formatNumber = num => num?.toLocaleString() || '0';
const formatSeconds = seconds => {
  if (!seconds) return '0秒';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60) ;
  return mins > 0 ? `${mins}分${secs}秒` : `${secs}秒`;
};
const formatPercentage = item => item[currentIndicator.value.percentage]?.toFixed(2) || '0.00';
const formatValue = item => currentIndicator.value.formatter(item[currentIndicator.value.key]);

// 事件处理
const selectTimeRange = value => {
  selectedTimeRange.value = value;
};
const handleIndicatorChange = () => {
  fetchData();
};

// 自动刷新
watchEffect(fetchData);
</script>

<style scoped>
.dashboard {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.time-range {
  margin-bottom: 20px;
  display: flex;
  gap: 10px;
}

.time-range-button {
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  background: white;
  transition: all 0.2s;
}

.time-range-button.active {
  background: #42b983;
  color: white;
  border-color: #42b983;
}

.data-metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin: 20px 0;
}

.metric-item {
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  text-align: center;
}

.metric-label {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.indicator-switcher {
  margin: 20px 0;
}

.indicator-switcher select {
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #ddd;
  width: 200px;
}

.data-table {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

th {
  background: #f8f9fa;
  font-weight: 500;
}

.loading {
  padding: 20px;
  text-align: center;
  color: #666;
}
</style>